import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { User } from '../../types';
import { useTeacherTools } from '../contexts/TeacherToolsContext';
import './TeacherNavigation.css';
import { hasAnyAdminPermission } from '../../domains/auth/adminPermissions';
import { hasFullPlatformStaffStatus, hasOnboardingHomeScheduleAccess } from '../../domains/auth/teacherAccess';

const STAFF_HANDBOOK_PDF = '/docs/Overture_Teacher_Handbook_2026_v2.pdf';

type IconName = 'home' | 'onboarding' | 'learn' | 'book' | 'library' | 'games' | 'admin' | 'profile' | 'logout';

type NavRouteItem = { kind: 'route'; to: string; label: string; icon: IconName; end?: boolean };
type NavHandbookItem = { kind: 'handbook'; label: string; icon: IconName };
type NavItem = NavRouteItem | NavHandbookItem;

interface TeacherNavigationProps {
  userData: User | null;
  onLogout: () => Promise<void>;
  isHome: boolean;
}

const TeacherNavIcon: React.FC<{ name: IconName }> = ({ name }) => {
  const paths: Record<IconName, React.ReactNode> = {
    home: <><path d="M3 10.8 12 3l9 7.8" /><path d="M5.5 9.5V21h13V9.5M9.5 21v-7h5v7" /></>,
    onboarding: <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 3.5h6v3H9zM8.5 12l2 2 5-5M8 18h8" /></>,
    learn: <><path d="m3 8 9-4 9 4-9 4-9-4Z" /><path d="M7 10.2v5.2c2.8 2.1 7.2 2.1 10 0v-5.2M21 8v7" /></>,
    book: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" /></>,
    library: <path d="M12 2.8c.88 5.2 3.98 8.3 9.2 9.2-5.22.9-8.32 4-9.2 9.2-.88-5.2-3.98-8.3-9.2-9.2 5.22-.9 8.32-4 9.2-9.2Z" style={{ fill: 'currentColor', stroke: 'none' }} />,
    games: <><path d="M8.2 8.5h7.6a5.2 5.2 0 0 1 5 6.6l-1.1 3.6a2 2 0 0 1-3.2 1l-2.1-1.8H9.6l-2.1 1.8a2 2 0 0 1-3.2-1l-1.1-3.6a5.2 5.2 0 0 1 5-6.6Z" /><path d="M8 12v4M6 14h4M16.5 12.5h.01M18.5 15h.01" /></>,
    admin: <><path d="M12 3 4.5 6v5.2c0 4.8 3.1 8.2 7.5 9.8 4.4-1.6 7.5-5 7.5-9.8V6L12 3Z" /><path d="M9 12h6M12 9v6" /></>,
    profile: <><circle cx="12" cy="8" r="4" /><path d="M4.5 21a7.5 7.5 0 0 1 15 0" /></>,
    logout: <><path d="M10 4H5v16h5M14 8l4 4-4 4M18 12H9" /></>,
  };

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {paths[name]}
    </svg>
  );
};

const TeacherNavigation: React.FC<TeacherNavigationProps> = ({ userData, onLogout, isHome }) => {
  const location = useLocation();
  const { tools } = useTeacherTools();
  const menuRef = useRef<HTMLDivElement>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isHandbookOpen, setIsHandbookOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const isAdmin = userData?.isAdmin === true;
  const hasAdminTools = hasAnyAdminPermission(userData);
  const onboardingInProgress = ['offer_sent', 'offer_accepted', 'onboarding']
    .includes(String((userData as any)?.hiringStatus || ''));
  const isRestrictedTeacher = !isAdmin
    && (!hasFullPlatformStaffStatus(userData?.staffStatus) || onboardingInProgress);
  const canViewHomeSchedule = hasOnboardingHomeScheduleAccess(userData);
  const displayName = String(userData?.displayName || 'Teacher').trim() || 'Teacher';
  const initials = displayName
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');

  useEffect(() => {
    setIsProfileOpen(false);
    setIsHandbookOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const closeMenu = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', closeMenu);
    return () => document.removeEventListener('mousedown', closeMenu);
  }, []);

  useEffect(() => {
    if (!isHandbookOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsHandbookOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isHandbookOpen]);

  const items: NavItem[] = [
    {
      kind: 'route',
      to: isRestrictedTeacher && !canViewHomeSchedule ? '/app/onboarding' : '/app/squads',
      label: isRestrictedTeacher && !canViewHomeSchedule ? 'Onboarding' : 'Home',
      icon: isRestrictedTeacher && !canViewHomeSchedule ? 'onboarding' : 'home',
      end: true,
    },
    { kind: 'route', to: '/app/resources', label: 'Certifications', icon: 'learn' },
    { kind: 'handbook', label: 'Handbook', icon: 'book' },
    ...(!isRestrictedTeacher
      ? [
          { kind: 'route' as const, to: '/app/games', label: 'Quick Games', icon: 'games' as IconName },
          { kind: 'route' as const, to: '/app/library', label: 'Universe', icon: 'library' as IconName },
        ]
      : []),
    ...(hasAdminTools
      ? [{ kind: 'route' as const, to: '/app/admin', label: isAdmin ? 'Admin' : 'Admin Tools', icon: 'admin' as IconName }]
      : []),
  ];

  const signOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      await onLogout();
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <aside className={`teacher-navigation ${isHome ? 'teacher-navigation--expanded' : 'teacher-navigation--rail'}`}>
      <div className="teacher-navigation__inner">
        <div className="teacher-navigation__profile" ref={menuRef}>
          <button
            type="button"
            className="teacher-navigation__profile-button"
            onClick={() => setIsProfileOpen(open => !open)}
            aria-expanded={isProfileOpen}
            aria-haspopup="menu"
            title={isHome ? undefined : displayName}
          >
            <span className="teacher-navigation__avatar">
              {(userData as any)?.headshotUrl ? (
                <img src={(userData as any).headshotUrl} alt="" />
              ) : initials}
            </span>
            {isHome && (
              <span className="teacher-navigation__profile-copy">
                <strong>{displayName}</strong>
                <span>View profile</span>
              </span>
            )}
            {isHome && <span className="teacher-navigation__more" aria-hidden="true">•••</span>}
          </button>

          {isProfileOpen && (
            <div className="teacher-navigation__profile-menu" role="menu">
              <NavLink to="/app/profile" className="teacher-navigation__menu-item" role="menuitem">
                <TeacherNavIcon name="profile" />
                Profile
              </NavLink>
              <button
                type="button"
                className="teacher-navigation__menu-item teacher-navigation__menu-item--logout"
                onClick={signOut}
                disabled={isSigningOut}
                role="menuitem"
              >
                <TeacherNavIcon name="logout" />
                {isSigningOut ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          )}
        </div>

        <nav className="teacher-navigation__links" aria-label="Teacher navigation">
          {items.map(item => item.kind === 'handbook' ? (
            <button
              key="handbook"
              type="button"
              className={`teacher-navigation__link ${isHandbookOpen ? 'is-active' : ''}`}
              onClick={() => {
                setIsProfileOpen(false);
                setIsHandbookOpen(true);
              }}
              title={isHome ? undefined : item.label}
            >
              <TeacherNavIcon name={item.icon} />
              <span>{item.label}</span>
            </button>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `teacher-navigation__link ${isActive ? 'is-active' : ''}`}
              title={isHome ? undefined : item.label}
            >
              <TeacherNavIcon name={item.icon} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {tools.length > 0 && (
          <div className="teacher-navigation__tools" aria-label="Classroom tools">
            {!isHome && <div className="teacher-navigation__tools-rule" aria-hidden="true" />}
            {tools.map(tool => (
              <button
                key={tool.id}
                type="button"
                className="teacher-navigation__tool"
                onClick={tool.onClick}
                title={tool.label}
                aria-label={tool.label}
              >
                <span className="teacher-navigation__tool-emoji" aria-hidden="true">
                  {tool.emoji}
                </span>
                {isHome && <span className="teacher-navigation__tool-label">{tool.label}</span>}
              </button>
            ))}
          </div>
        )}

        {isHome && (
          <div className="teacher-navigation__note">
            Classroom tools appear in the rail while teaching.
          </div>
        )}
      </div>

      {isHandbookOpen && createPortal(
        <div
          className="teacher-handbook-overlay"
          onClick={() => setIsHandbookOpen(false)}
          role="presentation"
        >
          <div
            className="teacher-handbook-modal"
            onClick={event => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="teacher-handbook-title"
          >
            <div className="teacher-handbook-modal__header">
              <strong id="teacher-handbook-title">Handbook</strong>
              <div className="teacher-handbook-modal__actions">
                <a href={STAFF_HANDBOOK_PDF} target="_blank" rel="noreferrer">Open in new tab</a>
                <button
                  type="button"
                  className="teacher-handbook-modal__close"
                  onClick={() => setIsHandbookOpen(false)}
                  aria-label="Close handbook"
                >
                  ✕
                </button>
              </div>
            </div>
            <iframe
              className="teacher-handbook-modal__frame"
              title="Staff handbook"
              src={STAFF_HANDBOOK_PDF}
            />
          </div>
        </div>,
        document.body
      )}
    </aside>
  );
};

export default TeacherNavigation;
